import Uploader from '@components/Uploader';
import { css } from '@emotion/react';
import {
  Banner,
  Button,
  Card,
  FormGroup,
  Input,
  spacing,
  Typography,
  color,
  ButtonGroup,
  Modal
} from '@uniquegood/realworld-studio-design';
import { useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { Controller, useForm } from 'react-hook-form';
import {
  ClickEventName,
  InputEventName,
  PageViewEventName,
  PromotionTriggerLocation,
  track,
  UploadEventName
} from '@/track';
import { coreApi } from '@/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useModalState, useRequest, useRestrictProjectRoute, usePromotionModal } from '@/hooks';
import { CardItemGapWithOutHeader, MarginBottom4 } from '@/styles';
import { container650Style } from '@/styles/containerStyles';
import { helpLink } from '@/utils';
import { centeredStyle } from '@/components/containers';
import { checkBorder, getMaxPageNumber, getSlotRange } from './pagination';
import { TemplateItem } from './components/TemplateItem/TemplateItem';
import { EmptyStateTemplateItem } from './components/TemplateItem';

import { TemplateDetailModal } from './components/TemplateDetailModal';
import { usePlanLimits } from '@/hooks/usePlanLimits';

const templateEmptyState: Pick<ProjectTemplateResponseModel, 'id' | 'name' | 'imageUrl'> = {
  id: 'DEFAULT',
  name: '템플릿 없이 시작하기',
  imageUrl: ''
};

type FormData = CreateProjectRequest;

function ProjectsCreate() {
  useEffect(() => {
    track.onPageView({ pageViewEventName: PageViewEventName.view_createnewgame });
  }, []);

  const { appId } = useParams<AppParam>();
  const history = useHistory();

  const redirectTo = useRestrictProjectRoute();
  const { control, handleSubmit, watch, formState, setValue } = useForm<FormData>();

  const { data: { data: templates } = { data: [] } } = useRequest<
    CommonResponseModel<ProjectTemplateResponseModel[]>
  >(`/apps/${appId}/projects/templates`);

  const [templatePagingIndex, setTemplatePagingIndex] = useState(0);

  const { openModal, closeModal, modal } = useModalState();
  const { plan } = usePlanLimits();
  const { openPromotionModal } = usePromotionModal();
  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);
  const { data: me, mutate: mutateMe } = useRequest<MyInfo>('/api/me');

  function applyTemplate(templateId: string) {
    setValue('templateId', templateId);
    closeModal();
  }

  const handleOpenPlanButtonClick = () => {
    closeModal();
    openPromotionModal({ triggerLocation: PromotionTriggerLocation.template_detail });
  };

  const handleDetailClick = async (template: ProjectTemplateResponseModel) => {
    let applicationInfoData = applicationInfo;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: applicationInfoData?.data.id,
      channel_name: applicationInfoData?.data.name ?? '',
      plan_type: applicationInfoData?.data.planType,
      use_purpose_type: applicationInfoData?.data.usePurposeType ?? '',
      templete: template.name,
      user_id: meInfo?.id
    };

    track.onClick({
      clickEventName: ClickEventName.click_newgame_templete_detail,
      params: param
    });

    const canAccess = template.availablePlans.includes(plan);

    if (!canAccess) {
      openModal({
        title: '템플릿 자세히 보기',
        size: 'medium',
        onClose: closeModal,
        children: (
          <TemplateDetailModal
            title={template.name ?? ''}
            imageUrl={template.imageUrl}
            description={template.description ?? ''}
            additionalDescription={template.details ?? ''}
            usedTechs={template.usedTechnologis}
            plans={template.availablePlans.filter((plan) => plan !== 'Realworld')}
            previewImages={template.previewImageUrls}
          />
        ),
        customActionButton: (
          <>
            <Button
              type="basic"
              size="large"
              onClick={handleOpenPlanButtonClick}
              cssStyle={css`
                margin-right: 8px;
              `}
            >
              요금제 보러가기
            </Button>
            <Button
              type="basic"
              disabled
              size="large"
              onClick={() => {
                closeModal();
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_templete_detail_select,
                  params: param
                });
              }}
            >
              템플릿 선택하기
            </Button>
          </>
        )
      });
      return;
    }

    openModal({
      title: '템플릿 자세히 보기',
      size: 'medium',
      onClose: closeModal,
      children: (
        <TemplateDetailModal
          title={template.name ?? ''}
          imageUrl={template.imageUrl ?? ''}
          description={template.description ?? ''}
          additionalDescription={template.details ?? ''}
          usedTechs={template.usedTechnologis}
          plans={template.availablePlans.filter((plan) => plan !== 'Realworld')}
          previewImages={template.previewImageUrls}
        />
      ),
      primaryAction: {
        content: '템플릿 선택하기',
        onAction: () => {
          applyTemplate(template.id ?? '');
          track.onClick({
            clickEventName: ClickEventName.click_newgame_templete_detail_select,
            params: param
          });
        }
      }
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    const { data: projectData } = await coreApi.post<CommonResponseModel<ProjectV2>>(
      `/apps/${appId}/projects`,
      {
        ...data,
        templateId: data.templateId === 'DEFAULT' ? undefined : data.templateId,
        projectEngineVersion: 'V1'
      },
      {
        headers: {
          'x-rwd-api-version': '1.1'
        }
      }
    );
    track.onClick({
      clickEventName: ClickEventName.click_newgame_button_creategame,
      params: { templateId: data.templateId }
    });

    const project = projectData.data;

    history.push(`/apps/${appId}/projects/${project.id}/detail`);
  });

  if (redirectTo === undefined) return <LoadingSpinner />;

  if (typeof redirectTo === 'string') return <Redirect to={redirectTo} />;

  const [name, horizontalImageId] = watch(['name', 'horizontalImageId']);

  const maxPage = getMaxPageNumber((templates?.length ?? 0) + 1, PAGING_RANGE_ITEM);

  const slotRange = getSlotRange(templatePagingIndex, PAGING_RANGE, maxPage);

  const [leftChevronButtonDisable, rightChevronButtonDisable] = checkBorder(
    templatePagingIndex,
    maxPage
  );

  return (
    <form onSubmit={onSubmit} css={[ContainerLayout, container650Style, centeredStyle]}>
      <Modal {...modal} />
      <Typography as="h1" type="displayLarge">
        새 콘텐츠 만들기
      </Typography>
      <Card cssStyle={CardItemGapWithOutHeader}>
        <FormGroup label="콘텐츠 이름" requiredIndicator>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                placeholder="예) 태양단의 비밀"
                showCharacterCount
                maxLength={100}
                {...field}
                onChange={(...args) => {
                  field.onChange(...args);
                  track.onInputThrottled({
                    inputEventName: InputEventName.input_newgame_name
                  });
                }}
              />
            )}
          />
        </FormGroup>
        <FormGroup label="콘텐츠 대표 이미지" requiredIndicator>
          <Controller
            control={control}
            name="horizontalImageId"
            rules={{ required: true }}
            render={({ field: { onChange } }) => (
              <Uploader
                getUploadFiles={(files) => {
                  if (files?.length) {
                    onChange(files?.[0].id);
                    track.onUpload({
                      uploadEventName: UploadEventName.upload_newgame_mainimage
                    });
                  }
                }}
                hasSearch
                ratio="horizontal"
              />
            )}
          />
        </FormGroup>
        <FormGroup label="한 줄 설명">
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Input
                placeholder="예) 한반도를 지키는 아홉개의 국보를 찾아라!"
                showCharacterCount
                maxLength={200}
                {...field}
                onChange={(...args) => {
                  field.onChange(...args);
                  track.onInputThrottled({
                    inputEventName: InputEventName.input_newgame_name
                  });
                }}
              />
            )}
          />
        </FormGroup>
      </Card>
      <Card cssStyle={templateSectionContainer}>
        {templates ? (
          <>
            <Controller
              control={control}
              name="templateId"
              rules={{ required: true }}
              defaultValue={templateEmptyState.id ?? 'DEFAULT'}
              render={({ field: { value, onChange } }) => (
                <FormGroup label="템플릿 선택" requiredIndicator cssStyle={templateSectionLayout}>
                  {[templateEmptyState, ...templates]
                    .slice(
                      templatePagingIndex * PAGING_RANGE_ITEM,
                      templatePagingIndex * PAGING_RANGE_ITEM + PAGING_RANGE_ITEM
                    )
                    .map((template) =>
                      template.id === 'DEFAULT' ? (
                        <EmptyStateTemplateItem
                          key={template.id}
                          id={template.id}
                          name={template.name ?? ''}
                          checked={(template.id ?? '') === value}
                          onChange={(...args) => {
                            onChange(...args);
                            track.onClick({
                              clickEventName: ClickEventName.click_newgame_select_notemplate
                            });
                          }}
                        />
                      ) : (
                        <TemplateItem
                          key={template.id}
                          id={template.id}
                          name={template.name}
                          imageUrl={template.imageUrl}
                          checked={(template.id ?? '') === value}
                          onChange={(...args) => {
                            onChange(...args);
                            track.onClick({
                              clickEventName: ClickEventName.click_newgame_select_template,
                              params: { templateId: template.id, name: template.name }
                            });
                          }}
                          onDetailClick={() => {
                            handleDetailClick(template as ProjectTemplateResponseModel);
                          }}
                          radioButtonDisabled={
                            !(template as ProjectTemplateResponseModel).availablePlans.includes(
                              plan
                            )
                          }
                        />
                      )
                    )}
                </FormGroup>
              )}
            />
            {(templates?.length ?? 0) + 1 > PAGING_RANGE_ITEM && (
              <ButtonGroup cssStyle={buttonGroup}>
                <Button
                  icon="chevron_right_regular"
                  type="plain"
                  size="small"
                  disabled={leftChevronButtonDisable}
                  onClick={() => setTemplatePagingIndex((index) => (index === 0 ? 0 : index - 1))}
                  cssStyle={chevronRight}
                />
                {slotRange.map((index) => (
                  <Button
                    key={index}
                    onClick={() => setTemplatePagingIndex(index)}
                    type="plain"
                    size="small"
                    cssStyle={templatePagingIndex === index ? selectedPageIndexStyle : undefined}
                  >
                    {(index + 1).toString()}
                  </Button>
                ))}
                <Button
                  icon="chevron_right_regular"
                  size="small"
                  type="plain"
                  disabled={rightChevronButtonDisable}
                  onClick={() =>
                    setTemplatePagingIndex((index) => (index === maxPage ? maxPage : index + 1))
                  }
                />
              </ButtonGroup>
            )}
          </>
        ) : (
          <LoadingSpinner />
        )}
      </Card>
      {NoticeBanner}
      <Button
        type="primary"
        size="large"
        disabled={!name || !horizontalImageId || name.replace(/\s/g, '').length === 0}
        htmlType="submit"
        loading={formState.isSubmitting}
      >
        콘텐츠 생성
      </Button>
    </form>
  );
}

export default ProjectsCreate;

const chevronRight = css`
  svg {
    transform: rotate(180deg);
  }
`;

const ContainerLayout = css`
  display: flex;
  flex-direction: column;

  button[type='submit'] {
    align-self: center;
  }

  > * {
    margin-bottom: ${spacing.margin.xlarge2};
  }

  margin-bottom: ${spacing.margin.xlarge4};
`;

const NoticeBanner = (
  <Banner
    header={
      <Typography as="h2" type="heading" cssStyle={MarginBottom4}>
        콘텐츠 제작 시 아래의 사항을 유의해주세요
      </Typography>
    }
  >
    <>
      <ul
        css={css`
          li:before {
            content: '❌';
            margin-right: 0.25em;
          }
        `}
      >
        <Typography as="li" type="body">
          선정적이거나 불쾌감을 주는 이미지
        </Typography>
        <Typography as="li" type="body">
          타인의 저작권, 개인정보 또는 초상권 침해
        </Typography>
        <Typography as="li" type="body">
          위험한 행동을 조장하는 내용
        </Typography>
      </ul>
      <Typography
        as="p"
        type="body"
        cssStyle={css`
          margin-top: 1em;
        `}
      >
        위 내용에 동의할 경우 [콘텐츠 생성] 버튼을 눌러주세요. 위반 시&nbsp;
        <Button url={helpLink.notion.guideline} external>
          가이드라인
        </Button>
        에 따라 제재를 받을 수 있어요.
      </Typography>
    </>
  </Banner>
);

const PAGING_RANGE_ITEM = 9;
const PAGING_RANGE = 3;

const templateSectionContainer = css`
  > div {
    display: flex;
    flex-direction: column;

    align-items: center;
  }
`;

const templateSectionLayout = css`
  > :last-of-type {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 24px;

    padding: ${spacing.padding.xlarge};

    @media (max-width: 632px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 435px) {
      grid-template-columns: repeat(1, 1fr);
    }
  }
`;

const buttonGroup = css`
  width: 188px;
  display: flex;
  justify-content: space-between;

  .Item {
    align-self: flex-end;
  }
`;

const selectedPageIndexStyle = css`
  color: ${color.palette_primary_purple_100};
  font-weight: 700;
`;
